/* eslint-disable no-console */
/**
=========================================================
* Material Dashboard 2 PRO React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

/** 
  All of the routes for the Material Dashboard 2 PRO React are added here,
  You can add a new route, customize the routes and delete the routes here.

  Once you add a new route on this file it will be visible automatically on
  the Sidenav.

  For adding a new route you can follow the existing routes in the routes array.
  1. The `type` key with the `collapse` value is used for a route.
  2. The `type` key with the `title` value is used for a title inside the Sidenav. 
  3. The `type` key with the `divider` value is used for a divider between Sidenav items.
  4. The `name` key is used for the name of the route on the Sidenav.
  5. The `key` key is used for the key of the route (It will help you with the key prop inside a loop).
  6. The `icon` key is used for the icon of the route on the Sidenav, you have to add a node.
  7. The `collapse` key is used for making a collapsible item on the Sidenav that contains other routes
  inside (nested routes), you need to pass the nested routes inside an array as a value for the `collapse` key.
  8. The `route` key is used to store the route location which is used for the react router.
  9. The `href` key is used to store the external links location.
  10. The `title` key is only for the item with the type of `title` and its used for the title text on the Sidenav.
  10. The `component` key is used to store the component of its route.
*/

// Material Dashboard 2 PRO React layouts

import Dashboard from "layouts/dashboard";
// import ProfileOverview from "layouts/pages/profile/profile-overview";
// import AllProjects from "layouts/pages/profile/all-projects";

import Settings from "layouts/account/mainUser";
import Clients from "layouts/clients";
import Projects from "layouts/projects";
import SupportCases from "layouts/supportCases/SupportCases";
import Notifications from "layouts/notifications/mainUser";
import Chat from "layouts/chat";
import Icon from "@mui/material/Icon";
import MDAvatar from "components/MDAvatar";
import AmenityEvents from "layouts/amenitiesEvents/AmenityEvents";
import ServiceEvents from "layouts/servicesEvents/ServiceEvents";
import Apartments from "layouts/project/components/apartments";
import Analytics from "layouts/analytics/index";

const routes = (user, projects) => {
  const {
    company,
    uid,
    isProjectManager,
    projects: userProjects,
    projectManagerFeatures,
    projectManagerDisplayName,
  } = user;
  // console.log("routesMainUser", { user, projects, isProjectManager, projects: userProjects });
  const { imageUrl = `https://onnaliving.com/userimagefiles/${uid}.png` } = user;

  let chatEnabled = false;
  let amenitiesEnabled = false;
  let servicesEnabled = false;
  let supportEnabled = false;
  let analyticsEnabled = false;

  const projectsArray = Object.values(projects || {});

  const accessibleProjects = projectsArray.filter((project) => {
    if (isProjectManager) {
      return userProjects[project.uid];
    }
    return true;
  });

  accessibleProjects.forEach((project) => {
    const { features = {} } = project;

    if (Object.hasOwn(features, "chat")) {
      const featureEnabled = features?.chat;
      chatEnabled =
        chatEnabled ||
        (isProjectManager ? featureEnabled && projectManagerFeatures?.chat : featureEnabled);
    }
    if (Object.hasOwn(features, "amenities")) {
      const featureEnabled = features?.amenities;
      amenitiesEnabled =
        amenitiesEnabled ||
        (isProjectManager ? featureEnabled && projectManagerFeatures?.amenities : featureEnabled);
    }
    if (Object.hasOwn(features, "services")) {
      const featureEnabled = features?.services;
      servicesEnabled =
        servicesEnabled ||
        (isProjectManager ? featureEnabled && projectManagerFeatures?.services : featureEnabled);
    }
    if (Object.hasOwn(features, "support")) {
      const featureEnabled = features?.support;
      supportEnabled =
        supportEnabled ||
        (isProjectManager ? featureEnabled && projectManagerFeatures?.support : featureEnabled);
    }
    if (Object.hasOwn(features, "analytics")) {
      const featureEnabled = features?.analytics;
      analyticsEnabled =
        analyticsEnabled ||
        (isProjectManager ? featureEnabled && projectManagerFeatures?.analytics : featureEnabled);
    }
  });

  const apartmentsEnabled = isProjectManager ? projectManagerFeatures?.apartments : true;

  const clientsEnabled = isProjectManager ? projectManagerFeatures?.clients : true;

  const notificationsEnabled = isProjectManager ? projectManagerFeatures?.notifications : true;

  const configurationEnabled = isProjectManager ? projectManagerFeatures?.configuration : true;

  const routesArray = [
    {
      type: "collapse",
      name: isProjectManager ? projectManagerDisplayName : company,
      key: "account",
      route: "/account",
      component: Settings,
      isProjectManager,
      icon: (
        <MDAvatar
          alt="profile-image"
          src={imageUrl}
          // size="sm"
          shadow="sm"
          bgColor="secondary"
        >
          <Icon fontSize="medium">person</Icon>
        </MDAvatar>
      ),
      noCollapse: true,
    },
    { type: "divider", key: "divider-0" },
    {
      type: "collapse",
      name: "dashboard",
      key: "dashboard",
      component: Dashboard,
      route: "/",
      icon: <Icon fontSize="medium">dashboard</Icon>,
      noCollapse: true,
    },
    {
      type: "collapse",
      name: "clientReservations",
      key: "clients",
      route: "/clients",
      component: Clients,
      icon: <Icon fontSize="medium">group</Icon>,
      noCollapse: true,
      hide: !clientsEnabled,
    },
    {
      type: "collapse",
      name: "apartments",
      key: "apartments",
      route: "/apartments",
      component: Apartments,
      icon: <Icon fontSize="medium">apartment</Icon>,
      noCollapse: true,
      hide: !apartmentsEnabled,
    },
    {
      type: "collapse",
      name: "support",
      key: "support",
      component: SupportCases,
      route: "/support",
      icon: <Icon fontSize="medium">support_agent_icon</Icon>,
      noCollapse: true,
      hide: !supportEnabled,
    },
    {
      type: "collapse",
      name: "amenityEvents",
      key: "amenities",
      component: AmenityEvents,
      route: "/amenities",
      icon: <Icon fontSize="medium">book</Icon>,
      noCollapse: true,
      hide: !amenitiesEnabled,
    },
    {
      type: "collapse",
      name: "serviceEvents",
      key: "services",
      component: ServiceEvents,
      route: "/services",
      icon: <Icon fontSize="medium">room_service</Icon>,
      noCollapse: true,
      hide: !servicesEnabled,
    },
    {
      type: "collapse",
      name: "chat",
      key: "chat",
      component: Chat,
      route: "/chat",
      icon: <Icon fontSize="medium">message</Icon>,
      noCollapse: true,
      hide: !chatEnabled,
    },

    {
      type: "collapse",
      name: "analytics",
      key: "analytics",
      route: "/analytics",
      component: Analytics,
      icon: <Icon fontSize="medium">analytics</Icon>,
      noCollapse: true,
      hide: !analyticsEnabled,
    },
    {
      type: "collapse",
      name: "notifications",
      key: "notifications",
      route: "/notifications",
      component: Notifications,
      icon: <Icon fontSize="medium">notifications</Icon>,
      noCollapse: true,
      hide: !notificationsEnabled,
    },
    { type: "divider", key: "divider-1" },

    {
      type: "collapse",
      name: "configuration",
      key: "projects",
      route: "/projects",
      component: Projects,
      icon: <Icon fontSize="medium">apartment</Icon>,
      noCollapse: false,
      hide: !configurationEnabled,
      collapse: accessibleProjects
        .map((project) => ({
          name: project?.name,
          key: project?.uid,
          route: `/projects/${project?.uid}`,
          imageUrl: project?.imageUid
            ? `https://onnaliving.com/userimagefiles/${uid}/${project.uid}/${project.imageUid}.png`
            : null,
        }))

        .filter(Boolean),
    },
  ];

  return routesArray;
};

export default routes;
